/**
 * Modal asking the admin to provide shipping details when an order moves to
 * "shipped" (Envoyée). Either:
 *  - carrier + tracking number (sends a trackable link in the customer email)
 *  - or pickup in store (sends a "ready to pick up" notice)
 */
import { useState } from 'react';
import { Truck, Store, X, Check } from 'lucide-react';

const CARRIERS = ['Colissimo', 'Chronopost', 'UPS', 'Mondial Relay', 'DPD', 'bpost', 'Neargo'];

export const ShipmentInfoModal = ({ open, orderId, onCancel, onConfirm, submitting }) => {
  const [method, setMethod] = useState('carrier');
  const [carrier, setCarrier] = useState('bpost');
  const [trackingNumber, setTrackingNumber] = useState('');

  if (!open) return null;

  const trimmed = trackingNumber.trim();
  const canSubmit = method === 'pickup' ||
    (method === 'carrier' && carrier && trimmed.length > 0);

  const submit = () => {
    if (!canSubmit) return;
    if (method === 'carrier') {
      onConfirm({ shipping_method: 'carrier', carrier, tracking_number: trimmed });
    } else {
      onConfirm({ shipping_method: 'pickup' });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="shipment-modal"
    >
      <div className="bg-[color:var(--color-surface)] border border-white/10 w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Confirmer l'expédition
            </h3>
            <p className="text-zinc-500 text-xs mt-0.5">
              Commande #{orderId?.slice(-8).toUpperCase()}
            </p>
          </div>
          <button type="button" onClick={onCancel}
            className="p-1 text-zinc-500 hover:text-white"
            data-testid="shipment-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Method picker */}
        <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-2">Mode d'envoi</p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button type="button" onClick={() => setMethod('carrier')}
            className={`p-3 border text-left transition-colors ${
              method === 'carrier'
                ? 'border-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]'
                : 'border-white/10 hover:border-white/30'
            }`}
            data-testid="method-carrier">
            <Truck className={`w-5 h-5 mb-1 ${method === 'carrier' ? 'text-[color:var(--color-primary)]' : 'text-zinc-500'}`} />
            <p className={`text-sm ${method === 'carrier' ? 'text-white' : 'text-zinc-400'}`}>
              Avec transporteur
            </p>
            <p className="text-zinc-500 text-[10px]">Suivi colis client</p>
          </button>
          <button type="button" onClick={() => setMethod('pickup')}
            className={`p-3 border text-left transition-colors ${
              method === 'pickup'
                ? 'border-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]'
                : 'border-white/10 hover:border-white/30'
            }`}
            data-testid="method-pickup">
            <Store className={`w-5 h-5 mb-1 ${method === 'pickup' ? 'text-[color:var(--color-primary)]' : 'text-zinc-500'}`} />
            <p className={`text-sm ${method === 'pickup' ? 'text-white' : 'text-zinc-400'}`}>
              Retrait en boutique
            </p>
            <p className="text-zinc-500 text-[10px]">Le client passe chercher</p>
          </button>
        </div>

        {/* Carrier picker + tracking input */}
        {method === 'carrier' && (
          <div className="space-y-3 mb-5">
            <div>
              <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-2">Transporteur</p>
              <div className="flex flex-wrap gap-2">
                {CARRIERS.map((c) => (
                  <button key={c} type="button" onClick={() => setCarrier(c)}
                    className={`px-3 py-1.5 text-xs border transition-colors ${
                      carrier === c
                        ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                        : 'border-white/10 text-zinc-400 hover:border-white/30'
                    }`}
                    data-testid={`carrier-${c.replace(/\s+/g, '-')}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-2">Numéro de suivi</p>
              <input type="text" value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Ex. 1Z999AA10123456784"
                autoFocus
                className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)] font-mono"
                data-testid="tracking-number-input" />
            </div>
          </div>
        )}

        {method === 'pickup' && (
          <div className="bg-amber-500/5 border border-amber-500/30 p-3 mb-5">
            <p className="text-amber-400 text-sm">
              Un email sera envoyé au client pour l'informer que sa commande
              est prête à être retirée en boutique.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={onCancel}
            className="flex-1 px-4 py-2.5 border border-white/10 text-zinc-400 hover:border-white/20 transition-colors text-sm"
            data-testid="shipment-cancel">
            Annuler
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit || submitting}
            className="flex-1 flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 hover:bg-[color:var(--color-primary-hover)] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="shipment-confirm">
            <Check className="w-4 h-4" />
            {submitting ? 'Envoi…' : 'Confirmer l\'expédition'}
          </button>
        </div>
      </div>
    </div>
  );
};
